const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Animation Enhancements
   Safe layer on top of Dawn animations.js.
   Keeps Dawn scroll triggers, design mode and zoom animation intact.
   ============================================================ */

(() => {
  const root = document.documentElement;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const CINEMATIC_REVEAL_SELECTOR = [
    '.shopify-section',
    '.banner',
    '.image-with-text',
    '.multicolumn-card',
    '.card-wrapper',
    '.article-card-wrapper',
    '.collection-list .grid__item',
    '.product__media-item',
    '.product__info-container',
    '.footer-block',
    '.vf-reveal-ready'
  ].join(',');

  const setAnimationState = (element, state, detail = {}) => {
    if (!element) return;

    element.dataset.vfAnimationState = state;
    element.classList.toggle('vf-animation-pending', state === 'pending');
    element.classList.toggle('vf-animation-visible', state === 'visible');
    element.classList.toggle('vf-animation-complete', state === 'complete');

    element.dispatchEvent(
      new CustomEvent(`vf:animation-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const prepareCinematicReveal = (rootEl = document) => {
    if (prefersReducedMotion.matches) {
      rootEl.querySelectorAll?.(CINEMATIC_REVEAL_SELECTOR).forEach((element) => {
        element.classList.add('vf-animation-visible', 'vf-animation-complete');
        element.dataset.vfAnimationState = 'complete';
      });
      return [];
    }

    return Array.from(rootEl.querySelectorAll?.(CINEMATIC_REVEAL_SELECTOR) || [])
      .filter((element) => !element.classList.contains('vf-animation-prepared'))
      .map((element, index) => {
        element.classList.add('vf-animation-prepared', 'vf-animation-pending');
        element.style.setProperty('--vf-animation-order', index % 12);
        setAnimationState(element, 'pending', { index });
        return element;
      });
  };

  const initializeCinematicReveal = (rootEl = document) => {
    const elements = prepareCinematicReveal(rootEl);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        setAnimationState(element, 'visible');
        window.setTimeout(() => setAnimationState(element, 'complete'), 500);
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, revealObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const element = entry.target;
          setAnimationState(element, 'visible', {
            ratio: entry.intersectionRatio
          });

          window.setTimeout(() => {
            setAnimationState(element, 'complete', {
              ratio: entry.intersectionRatio
            });
          }, 760);

          revealObserver.unobserve(element);
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.08
      }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const initializeCinematicParallax = () => {
    if (prefersReducedMotion.matches) return;

    const targets = Array.from(
      document.querySelectorAll(
        '.banner__media img, .slideshow__media img, .image-with-text__media img, .product__media img'
      )
    ).filter((element) => !element.__vfCinematicParallax);

    if (!targets.length) return;

    targets.forEach((element) => {
      element.__vfCinematicParallax = true;
      element.classList.add('vf-parallax-media');
    });

    const updateParallax = () => {
      targets.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        if (rect.bottom < 0 || rect.top > viewportHeight) return;

        const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
        const clamped = Math.min(Math.max(progress, 0), 1);
        const offset = (clamped - 0.5) * 18;

        element.style.setProperty('--vf-parallax-y', `${offset.toFixed(2)}px`);
      });
    };

    updateParallax();
    window.addEventListener('scroll', throttle(updateParallax, 50), { passive: true });
    window.addEventListener('resize', throttle(updateParallax, 150), { passive: true });
  };

  const initializeCinematicAnimations = (rootEl = document) => {
    root.classList.add('vf-animations-ready');
    root.classList.toggle('vf-reduced-motion', prefersReducedMotion.matches);

    initializeCinematicReveal(rootEl);

    if (rootEl === document) {
      initializeCinematicParallax();
    }
  };

  window.VFCinematicAnimations = {
    refresh(rootEl = document) {
      initializeCinematicAnimations(rootEl);
    },
    reveal(rootEl = document) {
      initializeCinematicReveal(rootEl);
    }
  };

  window.addEventListener('DOMContentLoaded', () => {
    initializeCinematicAnimations(document);
  });

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => initializeCinematicAnimations(event.target));
    document.addEventListener('shopify:section:select', (event) => initializeCinematicAnimations(event.target));
    document.addEventListener('shopify:block:select', (event) => {
      const target = event.target;
      target?.classList?.add('vf-animation-visible', 'vf-animation-complete');
    });
  }

  prefersReducedMotion.addEventListener?.('change', () => {
    root.classList.toggle('vf-reduced-motion', prefersReducedMotion.matches);
    if (prefersReducedMotion.matches) {
      document.querySelectorAll('.vf-animation-prepared').forEach((element) => {
        setAnimationState(element, 'complete');
      });
    }
  });
})();
